import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { Input } from '../common/FormFields';
import { Button } from '../common/Button';
import { validateField } from '../../utils/validation/signupValidation';
import { signIn } from '../../services/auth/authService';
import { logError, logInfo } from '../../utils/logger';

export const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = async () => {
    const errors = {};
    
    // Validate each field
    for (const [field, value] of Object.entries(formData)) {
      try {
        const validation = await validateField(field, value, {}, true);
        if (!validation.isValid) {
          errors[field] = validation.message;
        }
      } catch (err) {
        errors[field] = 'Validation failed';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setValidationErrors({});
    setLoading(true);

    try {
      // Validate form before submission
      const isValid = await validateForm();
      if (!isValid) {
        setLoading(false);
        return;
      }
      // Check if this email is pending verification
      const pendingEmail = localStorage.getItem('pendingEmail');
      if (pendingEmail === formData.email) {
        setShowVerification(true);
        setLoading(false);
        return;
      }

      await signIn(formData.email, formData.password);
      logInfo('User signed in successfully');
      navigate('/');
    } catch (error) {
      const errorMessage = error.code === 'auth/invalid-credential' 
        ? 'Invalid email or password'
        : 'Failed to sign in. Please try again.';
      setError(errorMessage);
      logError('Sign in error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = async (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    try {
      const validation = await validateField(field, value, {}, true);
      setValidationErrors(prev => ({
        ...prev,
        [field]: validation.isValid ? '' : validation.message
      }));
    } catch (err) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: 'Validation failed'
      }));
    }
  };

  return (
    <div className="min-h-screen bg-[#3D3F42] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src={`${window.location.origin}/logo.png`} 
            alt="OverSizeHub" 
            className="h-16"
            onError={(e) => {
              console.error('Logo failed to load in login form');
              e.target.src = `${window.location.origin}/favicon.png`;
            }}
          />
        </div>

        {/* Main Card */}
        <div className="bg-[#2A2B2E] rounded-lg p-8">
          <h2 className="text-2xl font-bold text-white mb-8">
            Sign in to your account
          </h2>

          {error && (
            <div className="bg-red-500/10 text-red-400 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              placeholder="Email Address"
              required
              icon={Mail}
              className="bg-gray-700/30 text-white placeholder-gray-400"
              error={validationErrors.email}
            />

            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={(e) => handleFieldChange('password', e.target.value)}
              placeholder="Password"
              required
              icon={Lock}
              className="bg-gray-700/30 text-white placeholder-gray-400"
              error={validationErrors.password}
            />

            <Button
              type="submit"
              loading={loading}
              disabled={loading}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
            >
              Sign In
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Don't have an account?{' '}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/plans', { replace: true });
                }}
                className="text-red-400 hover:text-red-300"
              >
                Create an account
              </button>
            </p>
          </div>
        </div>
      </div>
      {/* Verification Modal */}
      {showVerification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[300]">
          <div className="bg-[#2A2B2E] rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-white mb-4">Email Verification Required</h2>
            <p className="text-gray-400 mb-6">
              Please verify your email address to continue. Enter the verification code sent to {formData.email}.
            </p>
            <Button
              onClick={() => navigate('/verify')}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              Enter Verification Code
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};